import React, { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Grid } from '@mui/material'
import queryString from 'query-string'

import { useTitle } from 'hooks'

import LoginSidePanel from 'components/LoginSidePanel'

import { enabledFeatures } from 'config/env'
import backend, { isAuthenticated } from 'utils/backend'
import history from 'utils/history'
import { LOGIN } from 'utils/paths'

const OAUTH_RESUME_KEY = 'oauth-resume'

const OAuthAuthorizePageContent = () => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)
  const { search } = useLocation()
  // 'continue' is a reserved keyword in JS, so we alias it
  const { continue: continueToken } = queryString.parse(search)

  const [status, setStatus] = useState('loading') // 'loading' | 'consent' | 'error'
  const [clientName, setClientName] = useState('')
  const [scopes, setScopes] = useState([])
  const [isSubmittingDecision, setIsSubmittingDecision] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useTitle(t => t.oauth.title)

  const isOAuthFeatureEnabled = enabledFeatures.includes('OIDC_PROVIDER')

  const completeOAuth = useCallback(
    async decision => {
      try {
        const payload = {
          continue: continueToken,
          ...(decision ? { decision } : {})
        }
        const response = await backend.post('oauth/authorize/complete', payload)

        const redirectUrl = response.data.redirectTo || response.data.redirect_to
        const consentRequired = response.data.consentRequired || response.data.consent_required

        if (redirectUrl) {
          window.location.assign(redirectUrl)
          return
        }

        if (consentRequired) {
          setClientName(response.data.clientName || response.data.client_name || '')
          setScopes(response.data.scopes || [])
          setStatus('consent')
          return
        }

        setStatus('error')
        setErrorMessage(t.oauth.noRedirect)
      } catch (err) {
        const serverMessage =
          err?.response?.data?.detail || err?.response?.data?.error || err?.message
        setStatus('error')
        setErrorMessage(
          serverMessage ? `${t.oauth.authFailed} ${serverMessage}` : t.oauth.unexpectedError
        )
      }
    },
    [continueToken, t.oauth.authFailed, t.oauth.noRedirect, t.oauth.unexpectedError]
  )

  const submitConsentDecision = useCallback(
    async decision => {
      setIsSubmittingDecision(true)
      await completeOAuth(decision)
      setIsSubmittingDecision(false)
    },
    [completeOAuth]
  )

  useEffect(() => {
    if (!isOAuthFeatureEnabled) {
      history.replace('/')
      return
    }

    if (!continueToken) {
      setStatus('error')
      setErrorMessage(t.oauth.invalidRequest)
      return
    }

    if (!isAuthenticated()) {
      // Stash the full URL so the login flow can resume here after authentication
      window.localStorage.setItem(
        OAUTH_RESUME_KEY,
        window.location.pathname + window.location.search
      )
      history.push(LOGIN)
      return
    }

    completeOAuth()
  }, [continueToken, completeOAuth, isOAuthFeatureEnabled, t.oauth.invalidRequest])

  if (status === 'error') {
    return (
      <>
        <b>{t.oauth.title}</b>
        <h3>{t.oauth.errorTitle}</h3>
        <div className="message-danger">{errorMessage}</div>
      </>
    )
  }

  if (status === 'consent') {
    return (
      <>
        <b>{t.oauth.title}</b>
        <h3>{t.oauth.consentTitle}</h3>
        <p>
          {clientName
            ? t.oauth.consentBodyWithClient.replace('{clientName}', clientName)
            : t.oauth.consentBody}
        </p>
        {scopes.length > 0 && (
          <p>
            {t.oauth.requestedScopesLabel} <b>{scopes.join(', ')}</b>
          </p>
        )}
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <Button
            type="button"
            variant="outlined"
            disabled={isSubmittingDecision}
            onClick={() => submitConsentDecision('deny')}
          >
            {t.oauth.consentDeny}
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={isSubmittingDecision}
            onClick={() => submitConsentDecision('approve')}
          >
            {isSubmittingDecision ? t.oauth.consentSubmitting : t.oauth.consentApprove}
          </Button>
        </div>
      </>
    )
  }

  // 'loading' state — the POST is in flight or we are about to redirect
  return (
    <>
      <b>{t.oauth.title}</b>
      <h3>{t.oauth.connectingTitle}</h3>
      <p>{t.oauth.connectingBody}</p>
    </>
  )
}

const OAuthAuthorizePage = () => {
  return (
    <div className="Login">
      <Grid container className="Login-container">
        <LoginSidePanel />
        <div className="Login-content">
          <OAuthAuthorizePageContent />
        </div>
      </Grid>
    </div>
  )
}

export default OAuthAuthorizePage
